import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import MultiStyleButton from "./MultiStyleButton";
import DocumentViewer from "./DocumentViewer";
import "./Step9AuditReview.css";

import {
  getRiskClass,
  renderDataPoints,
  renderDocumentsReviewed,
  renderFinalDataPoints,
} from "./step9AuditReviewHelpers/Step9AuditReviewFunctions";

const Step9AuditReview = ({
  documentStepMemory,
  systemName,
  setStep,
  onCancel,
  onDataPointsUpdate,
  formData = {}, // Default to empty object
}) => {
  /* ------------------------------------------------------------------ */
  /*  Local state                                                        */
  /* ------------------------------------------------------------------ */
  const [viewingDocument, setViewingDocument] = useState(null);
  const [dataPointsState, setDataPointsState] = useState([]);
  const [showRejectionForm, setShowRejectionForm] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [revisedAuditStatus, setRevisedAuditStatus] = useState("");
  const [userRiskScore, setUserRiskScore] = useState("");
  const [updatedAuditRiskProfile, setUpdatedAuditRiskProfile] = useState(null);

  /* ------------------------------------------------------------------ */
  /*  Diagnostics                                                        */
  /* ------------------------------------------------------------------ */
  console.log(
    "[Step9AuditReview] rendered - documentStepMemory keys:",
    Object.keys(documentStepMemory || {})
  );

  /* Pretty-printed dump (collapsible in DevTools console) */
  Object.entries(documentStepMemory || {}).forEach(([k, v]) => {
    console.groupCollapsed(`📄 ${k}`);
    console.log(v);
    console.groupEnd();
  });

  /* ------------------------------------------------------------------ */
  /*  Derived data & effects                                             */
  /* ------------------------------------------------------------------ */
  const resolvedDataPoints = documentStepMemory?.resolvedDataPoints;
  // 🔄  DROP-IN replacement for the “flatten & set” effect
  useEffect(() => {
    if (!resolvedDataPoints || !resolvedDataPoints.dataPoints) return;

    /* ------------------------------------------------------------------
     1️⃣  NORMALISE whatever we got back into an array of datapoint objs
         – old model  : resolvedDataPoints.dataPoints === [...]
         – new model  : resolvedDataPoints.dataPoints === { field: value, … }
  -------------------------------------------------------------------*/
    let rawArray;
    if (Array.isArray(resolvedDataPoints.dataPoints)) {
      rawArray = resolvedDataPoints.dataPoints; // already good
    } else {
      // object → array
      rawArray = Object.entries(resolvedDataPoints.dataPoints).map(
        ([fieldName, value]) => {
          // If the value already looks like a data-point wrapper, keep it
          if (
            value &&
            typeof value === "object" &&
            !Array.isArray(value) &&
            "fieldName" in value
          ) {
            return value;
          }
          // Otherwise wrap minimally so the UI can edit it
          return {
            fieldName,
            value,
            status: "resolved",
          };
        }
      );
    }

    /* ------------------------------------------------------------------
     2️⃣  FLATTEN nested array-of-objects (e.g. education[]/experience[])
  -------------------------------------------------------------------*/
    const flattened = rawArray.flatMap((dp) => {
      if (
        Array.isArray(dp.value) &&
        dp.value.every((x) => typeof x === "object" && x !== null)
      ) {
        return dp.value.map((item, idx) => ({
          ...dp,
          fieldName: `${dp.fieldName}_${idx + 1}`,
          value: item,
          originalArrayField: dp.fieldName,
        }));
      }
      return dp;
    });

    setDataPointsState(flattened);
  }, [resolvedDataPoints]);

  /* Handy visual cue when a doc is opened/closed */
  useEffect(() => {
    console.log(
      viewingDocument ? "Now viewing:" : "No document is being viewed.",
      viewingDocument?.name || ""
    );
  }, [viewingDocument]);

  /* ------------------------------------------------------------------ */
  /*  Helpers                                                            */
  /* ------------------------------------------------------------------ */
  const auditRiskProfile =
    documentStepMemory?.auditRiskProfile?.auditRiskProfileJson
      ?.auditRiskProfile;

  if (!auditRiskProfile) return <div>Loading audit review…</div>;

  const extractedDataPointsResults =
    documentStepMemory?.extractedDataPointsResults;
  const structuredHTMLResults = documentStepMemory?.structuredHTMLResults;
  const isOCRdocument = documentStepMemory?.isOCRdocument;

  const displayAuditRiskProfile = updatedAuditRiskProfile
    ? updatedAuditRiskProfile
    : { auditor: auditRiskProfile };

  const hasRisks =
    Array.isArray(auditRiskProfile.riskElements) &&
    auditRiskProfile.riskElements.length &&
    !(
      auditRiskProfile.riskElements.length === 1 &&
      auditRiskProfile.riskElements[0] === "None"
    );

  /* ------------------------------------------------------------------ */
  /*  Mutators                                                           */
  /* ------------------------------------------------------------------ */
  const handleRejectDataPoint = (fieldName) =>
    setDataPointsState((prev) =>
      prev.map((dp) =>
        dp.fieldName === fieldName
          ? {
              ...dp,
              value: null,
              status: "unresolved",
              rejected: true,
              conflictingValues: dp.originalConflictingValues || [],
              previousValue: dp.value ?? dp.previousValue,
            }
          : dp
      )
    );

  const handleRestoreDataPoint = (fieldName) =>
    setDataPointsState((prev) =>
      prev.map((dp) =>
        dp.fieldName === fieldName
          ? {
              ...dp,
              value: dp.previousValue ?? dp.value,
              status: "resolved",
              rejected: false,
              conflictingValues: [],
            }
          : dp
      )
    );

  const handleSelectDataPoint = (fieldName, newVal) =>
    setDataPointsState((prev) =>
      prev.map((dp) =>
        dp.fieldName === fieldName
          ? {
              ...dp,
              value: newVal,
              status: "resolved",
              rejected: false,
              conflictingValues: [],
              originalConflictingValues:
                dp.originalConflictingValues || dp.conflictingValues,
            }
          : dp
      )
    );

  const handleViewDocument = (docName) => {
    let html = structuredHTMLResults?.[docName];
    if (!html) return console.warn("No HTML for", docName);
    html = html.replace(/```html\n?|```/g, ""); // strip code-fence
    setViewingDocument({ name: docName, content: html });
  };

  const handleContinue = () => {
    /* Re-construct any array fields that were flattened */
    const updatedDataPoints = dataPointsState.reduce((acc, dp) => {
      if (dp.value === null || dp.value === undefined) return acc;
      const key = dp.originalArrayField || dp.fieldName;
      if (dp.originalArrayField) {
        acc[key] = acc[key] || [];
        acc[key].push(dp.value);
      } else {
        acc[key] = dp.value;
      }
      return acc;
    }, {});

    console.log(
      "[Step9AuditReview] committing updated datapoints:",
      updatedDataPoints
    );

    onDataPointsUpdate(
      updatedDataPoints,
      updatedAuditRiskProfile || { auditor: auditRiskProfile }
    );
  };

  /* ------------------------------------------------------------------ */
  /*  Render                                                             */
  /* ------------------------------------------------------------------ */
  return (
    <div className="step9-audit-review">
      {/* ---------- AUDIT SUMMARY ---------- */}
      <div className="section">
        <h2>Document Audit Findings</h2>
        <div className="gold-separator" />
      </div>

      <div className="section audit-risk-summary">
        {updatedAuditRiskProfile && (
          <h3 className="rejected-heading">Rejected by User</h3>
        )}

        <h3>
          We have reviewed all submitted documents. Below are our comprehensive
          findings and the associated risk assessment for your {systemName}.
        </h3>

        {/* User rejection details (optional) */}
        {updatedAuditRiskProfile && (
          <div className="user-audit-details">
            <p>
              <strong>User’s Reason for Rejection:</strong>{" "}
              {updatedAuditRiskProfile.user.reason}
            </p>
            <p>
              <strong>User’s Revised Audit Status:</strong>{" "}
              <span
                className={getRiskClass(
                  updatedAuditRiskProfile.user.revisedAuditStatus
                )}
              >
                {updatedAuditRiskProfile.user.revisedAuditStatus}
              </span>
            </p>
            <p>
              <strong>User’s Risk Score:</strong>{" "}
              <span
                className={getRiskClass(
                  updatedAuditRiskProfile.user.userRiskScore
                )}
              >
                {updatedAuditRiskProfile.user.userRiskScore}
              </span>
            </p>
          </div>
        )}

        {/* Auditor details */}
        <div className="audit-details">
          <p>
            <strong>Audit Status:</strong>{" "}
            <span
              className={getRiskClass(
                displayAuditRiskProfile.auditor.auditStatus
              )}
            >
              {displayAuditRiskProfile.auditor.auditStatus}
            </span>
          </p>
          <p>
            <strong>Audit Overview:</strong>{" "}
            {displayAuditRiskProfile.auditor.auditOverview}
          </p>
          <p>
            <strong>Risk Score:</strong>{" "}
            <span
              className={getRiskClass(
                displayAuditRiskProfile.auditor.riskScore
              )}
            >
              {displayAuditRiskProfile.auditor.riskScore}
            </span>
          </p>
          <p>
            <strong>Risk Elements:</strong>{" "}
            {hasRisks
              ? displayAuditRiskProfile.auditor.riskElements.join(", ")
              : "None"}
          </p>
        </div>

        {!updatedAuditRiskProfile && (
          <div className="reject-findings">
            <p className="reject-label">
              <em>Don’t agree with the findings?</em>
            </p>
            <MultiStyleButton
              label="Reject findings"
              buttonType="cancel"
              onClick={() => setShowRejectionForm(true)}
            />
          </div>
        )}
      </div>

      {/* ---------- REJECTION FORM ---------- */}
      {showRejectionForm && (
        <div className="add-user-container">
          <h2 className="add-user-heading">Reject Audit Findings</h2>
          <form className="add-user-form">
            {/* reason */}
            <div className="form-group">
              <label className="form-label">
                Reason for Rejecting Audit Finding:
              </label>
              <textarea
                className="form-input"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter your reason..."
              />
            </div>
            {/* revised status */}
            <div className="form-group">
              <label className="form-label">Revised Audit Status:</label>
              <select
                className="form-select"
                value={revisedAuditStatus}
                onChange={(e) => setRevisedAuditStatus(e.target.value)}
              >
                <option value="">Select Status</option>
                <option value="Proceed">Proceed</option>
                <option value="Proceed with Caution">
                  Proceed with Caution
                </option>
                <option value="Advise Not to Proceed">
                  Advise Not to Proceed
                </option>
              </select>
            </div>
            {/* risk score */}
            <div className="form-group">
              <label className="form-label">User Risk Score:</label>
              <select
                className="form-select"
                value={userRiskScore}
                onChange={(e) => setUserRiskScore(e.target.value)}
              >
                <option value="">Select Risk Score</option>
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
              </select>
            </div>
            {/* buttons */}
            <div className="form-group">
              <MultiStyleButton
                label="Continue"
                buttonType="approve"
                onClick={() => {
                  setUpdatedAuditRiskProfile({
                    user: {
                      status: "Rejected",
                      reason: rejectionReason,
                      revisedAuditStatus,
                      userRiskScore,
                    },
                    auditor: auditRiskProfile,
                  });
                  setShowRejectionForm(false);
                }}
              />
              <MultiStyleButton
                label="Cancel"
                buttonType="cancel"
                onClick={() => {
                  setShowRejectionForm(false);
                  setRejectionReason("");
                  setRevisedAuditStatus("");
                  setUserRiskScore("");
                }}
              />
            </div>
          </form>
        </div>
      )}

      {/* ---------- DOCUMENTS REVIEWED ---------- */}
      {!viewingDocument ? (
        <div className="section documents-reviewed">
          <h2>Documents Reviewed</h2>
          <div className="gold-separator" />
          <h3>
            The following documents have been thoroughly reviewed and audited,
            with electronic versions generated for each.
          </h3>
          <div className="data-points-table-container">
            {renderDocumentsReviewed(
              extractedDataPointsResults,
              isOCRdocument,
              handleViewDocument
            )}
          </div>
        </div>
      ) : (
        /* HTML preview */
        <div className="document-viewer-container">
          <div className="document-viewer-header">
            <h2>{viewingDocument.name}</h2>
            <MultiStyleButton
              label="Close"
              buttonType="cancel"
              onClick={() => setViewingDocument(null)}
            />
          </div>
          <DocumentViewer htmlContent={viewingDocument.content} />
        </div>
      )}

      {/* ---------- DATA POINTS ---------- */}
      <div className="section extracted-datapoints">
        <h2>Extracted Datapoints</h2>
        <div className="gold-separator" />
        <h3>
          The extracted data points from your submitted documents have been
          validated and approved for auditor use.
        </h3>
        <div className="data-points-table-container">
          {renderDataPoints(
            dataPointsState,
            handleRejectDataPoint,
            handleSelectDataPoint,
            handleRestoreDataPoint
          )}
        </div>
      </div>

      <div className="section final-data-points">
        <h2>Final Data Points</h2>
        <div className="gold-separator" />
        <h3>
          Below are the final data points that will be added to the new record.
        </h3>
        <div className="data-points-table-container">
          {renderFinalDataPoints(dataPointsState)}
        </div>
      </div>

      {/* ---------- CONTINUE BUTTON ---------- */}
      <div className="step9-continue-button-container">
        <MultiStyleButton
          label="Continue"
          buttonType="approve"
          onClick={handleContinue}
        />
      </div>
    </div>
  );
};

Step9AuditReview.propTypes = {
  documentStepMemory: PropTypes.object.isRequired,
  systemName: PropTypes.string.isRequired,
  setStep: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onDataPointsUpdate: PropTypes.func.isRequired,
  formData: PropTypes.object,
};

export default Step9AuditReview;